import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Upload, X, Save, Trash2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { uploadAPI } from '../../utils/api';

const LogoSettings = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoPublicId, setLogoPublicId] = useState(''); // Stored public ID for saved logo
  const [uploadedPublicId, setUploadedPublicId] = useState(''); // Public ID for currently uploaded but not saved logo
  const [selectedFile, setSelectedFile] = useState(null); // Store the selected file for later upload
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [uploadController, setUploadController] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLogo();
  }, []);

  // Extract public ID from Cloudinary URL
  const extractPublicIdFromUrl = (url) => {
    if (!url) return null;
    try {
      // Extract public ID from Cloudinary URL
      // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
      const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
      if (match) {
        let publicId = match[1];
        // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
        publicId = publicId.replace(/\.[^/.]+$/, '');
        return publicId;
      }
      return null;
    } catch (error) {
      console.error('Error extracting public ID from URL:', error);
      return null;
    }
  };

  const fetchLogo = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/preferences/logo`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.value && data.value.trim() !== '') {
          setLogoUrl(data.value);
          setLogoPreview(data.value);
          // Extract and store public ID from saved logo URL
          const publicId = extractPublicIdFromUrl(data.value);
          if (publicId) {
            setLogoPublicId(publicId);
          }
        }
      } else if (response.status !== 404) {
        // 404 is handled by backend now, but keep this for backward compatibility
        console.error('Error fetching logo:', response.status);
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage(t('logoSettings.pleaseSelectImageFile'));
      setShowErrorModal(true);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setTimeout(() => {
        setMessage('');
        setShowErrorModal(false);
      }, 3000);
      return;
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setShowErrorModal(true);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Store the file for later upload
    setSelectedFile(file);
    setMessage('');

    // Create a local preview using FileReader
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result); // This will be a data URL for preview
    };
    reader.onerror = () => {
      setMessage(t('logoSettings.failedToPreviewImage'));
      setTimeout(() => setMessage(''), 3000);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!selectedFile && !logoPreview) {
      setMessage(t('logoSettings.pleaseUploadLogoFirst'));
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // If no file selected but there's a preview, it means we're using existing logo
    if (!selectedFile) {
      setMessage(t('logoSettings.noChangesToSave'));
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsLoading(true);
    setIsUploading(true);
    setMessage('');

    try {
      // If replacing an existing logo, delete the old one from Cloudinary first
      const oldPublicId = logoPublicId;
      const isReplacingLogo = logoUrl && oldPublicId;

      if (isReplacingLogo && oldPublicId) {
        try {
          console.log('🧹 Replacing logo: Deleting old logo from Cloudinary:', oldPublicId);
          await uploadAPI.deleteImage(oldPublicId);
          console.log('✅ Old logo deleted from Cloudinary successfully');
        } catch (deleteError) {
          console.error('❌ Error deleting old logo from Cloudinary:', deleteError);
          // Continue with saving new logo even if old logo deletion fails
        }
      }

      // Create abort controller for upload
      const controller = new AbortController();
      setUploadController(controller);

      // Upload the selected file to Cloudinary
      console.log('📤 Uploading logo to Cloudinary...');
      const result = await uploadAPI.uploadImage(selectedFile, controller.signal, 'logos');
      
      console.log('✅ Logo uploaded to Cloudinary:', result.imageUrl);
      const uploadedLogoUrl = result.imageUrl;
      const uploadedPublicId = result.publicId;

      // Save logo URL to preferences
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          key: 'logo',
          value: uploadedLogoUrl
        })
      });

      if (response.ok) {
        // Update state with uploaded logo
        setLogoUrl(uploadedLogoUrl);
        setLogoPreview(uploadedLogoUrl);
        setLogoPublicId(uploadedPublicId);
        setSelectedFile(null); // Clear selected file
        setUploadedPublicId(''); // Clear uploaded public ID
        setMessage(t('logoSettings.logoSavedSuccessfully'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        // If saving preference fails, delete the uploaded image from Cloudinary
        try {
          console.log('⚠️ Failed to save preference, deleting uploaded image from Cloudinary');
          await uploadAPI.deleteImage(uploadedPublicId);
        } catch (deleteError) {
          console.error('❌ Error cleaning up uploaded image:', deleteError);
        }
        const errorData = await response.json();
        setMessage(errorData.message || t('logoSettings.failedToSaveLogo'));
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Upload cancelled by user');
        setMessage(t('logoSettings.uploadCancelled'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        console.error('Error saving logo:', error);
        setMessage(t('logoSettings.failedToSaveLogo'));
        setTimeout(() => setMessage(''), 3000);
      }
    } finally {
      setIsLoading(false);
      setIsUploading(false);
      setUploadController(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async () => {
    if (!logoUrl && !logoPreview) return;

    setIsLoading(true);
    setMessage('');

    try {
      // Delete image from Cloudinary first (if we have a saved logo)
      if (logoPublicId) {
        try {
          console.log('🧹 Deleting logo from Cloudinary:', logoPublicId);
          await uploadAPI.deleteImage(logoPublicId);
          console.log('✅ Logo deleted from Cloudinary successfully');
        } catch (deleteError) {
          console.error('❌ Error deleting logo from Cloudinary:', deleteError);
          // Continue with removing preference even if Cloudinary deletion fails
        }
      } else if (logoUrl) {
        // Try to extract public ID from URL as fallback
        const publicId = extractPublicIdFromUrl(logoUrl);
        if (publicId) {
          try {
            console.log('🧹 Deleting logo from Cloudinary (extracted from URL):', publicId);
            await uploadAPI.deleteImage(publicId);
            console.log('✅ Logo deleted from Cloudinary successfully');
          } catch (deleteError) {
            console.error('❌ Error deleting logo from Cloudinary:', deleteError);
          }
        }
      }

      // Remove logo preference from database
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          key: 'logo',
          value: '' // Empty string to remove logo
        })
      });

      if (response.ok) {
        setLogoUrl('');
        setLogoPreview('');
        setLogoPublicId('');
        setUploadedPublicId('');
        setMessage(t('logoSettings.logoRemovedSuccessfully'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || t('logoSettings.failedToRemoveLogo'));
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error removing logo:', error);
      setMessage(t('logoSettings.failedToRemoveLogo'));
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Cancel any ongoing upload
    if (uploadController) {
      uploadController.abort();
      setUploadController(null);
    }

    // Reset to current saved logo (if any)
    setLogoPreview(logoUrl || '');
    setSelectedFile(null); // Clear selected file
    setUploadedPublicId('');
    setIsUploading(false);
    setMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const cancelUpload = () => {
    if (uploadController) {
      uploadController.abort();
      setIsUploading(false);
      setUploadController(null);
    }
  };

  const hasChanges = selectedFile !== null; // Changes exist if a file has been selected

  return (
    <div>
      <div className={`flex items-center mb-4 ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
        <Image className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-medium text-card-foreground">{t('logoSettings.title')}</h3>
      </div>
      
      <div className="space-y-3">
        {/* Current Logo Preview */}
        {(logoUrl || logoPreview) && (
          <div className="flex flex-col items-center p-4 bg-muted/50 rounded-lg border border-border">
            <div className="mb-3 relative">
              <img
                src={logoPreview || logoUrl}
                alt="Logo preview"
                className="max-w-full max-h-32 object-contain rounded-md"
              />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {selectedFile && !logoUrl ? t('logoSettings.logoPreview') : t('logoSettings.currentLogo')}
            </p>
          </div>
        )}

        {/* Upload Button */}
        <div>
          <label className="block text-xs font-medium text-card-foreground mb-2">
            {t('logoSettings.uploadLogo')}
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="logo-upload"
          />
          <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <label
              htmlFor="logo-upload"
              className={`flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border cursor-pointer transition-colors ${
                isLoading || isUploading
                  ? 'bg-muted border-border text-muted-foreground cursor-not-allowed'
                  : 'bg-background border-border text-card-foreground hover:bg-muted hover:border-primary'
              }`}
            >
              <Upload className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              {t('logoSettings.selectFile')}
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        {hasChanges && (
          <div className={`flex ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <button
              onClick={handleSave}
              disabled={isLoading || isUploading || !selectedFile}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading || isUploading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                  {isUploading ? t('logoSettings.uploading') : t('logoSettings.saving')}
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  {t('logoSettings.save')}
                </div>
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading || isUploading}
              className="px-4 py-2 text-sm font-medium text-card-foreground bg-background border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('logoSettings.cancel')}
            </button>
          </div>
        )}

        {/* Remove Button */}
        {(logoUrl || logoPreview) && !hasChanges && (
          <button
            onClick={handleRemove}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-destructive bg-background border border-destructive/20 rounded-md hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center">
              <Trash2 className="h-4 w-4 mr-2" />
              {t('logoSettings.removeLogo')}
            </div>
          </button>
        )}

        {/* Message */}
        {message && (
          <div className={`text-xs p-2 rounded-md ${
            message.includes('successfully') || message.includes('Successfully')
              ? 'bg-chart-3/10 text-chart-3 border border-chart-3/20' 
              : 'bg-destructive/10 text-destructive border border-destructive/20'
          }`}>
            {message}
          </div>
        )}

        {/* Error Modal Popup */}
        {showErrorModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-card w-full max-w-md rounded-xl shadow-2xl border border-border/50 p-8 mx-4 transform transition-all duration-300 ease-out scale-100">
              {/* Header with icon */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  </div>
                  <h4 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h4>
                </div>
                <button 
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted/50" 
                  onClick={() => setShowErrorModal(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Message */}
              <div className="mb-8">
                <p className="text-sm text-card-foreground leading-relaxed">{t('logoSettings.imageSizeLimit')}</p>
              </div>
              
              {/* Action button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="px-6 py-2.5 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors shadow-sm hover:shadow-md"
                >
                  {t('common.ok')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="text-xs text-muted-foreground">
          <p>• {t('logoSettings.logoUploadDescription')}</p>
          <p>• {t('logoSettings.recommendedSize')}</p>
          <p>• {t('logoSettings.supportedFormats')}</p>
        </div>
      </div>
    </div>
  );
};

export default LogoSettings;
